import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GIRAFFE_MASCOT = '/images/simby-mascote.png';
const DEFAULT_VIDEO_ID = 'Ll_TBWIXvS8';

// Extrai o ID de um vídeo do YouTube a partir de qualquer formato de link
// (watch?v=, youtu.be/, /embed/). Cai no vídeo padrão se não reconhecer.
const extractYouTubeId = (url) => {
  if (!url) return DEFAULT_VIDEO_ID;
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  // Se já for só o ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) return url.trim();
  return DEFAULT_VIDEO_ID;
};

// ══════════════════════════════════════════════════════════════════════════
// Onboarding enxuto (Simby-3): Cadastro → vídeo de boas-vindas → Dashboard.
// As perguntas de personalização, o Perfil Relacional e o PDI saíram daqui —
// agora ficam acessíveis por escolha na aba "Plano de Desenvolvimento".
// ══════════════════════════════════════════════════════════════════════════
const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [videoId, setVideoId] = useState(DEFAULT_VIDEO_ID);
  const [isLeaving, setIsLeaving] = useState(false);

  // Busca o vídeo configurado pelo admin (público, sem auth necessária)
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/settings/public`)
      .then((res) => {
        if (active) setVideoId(extractYouTubeId(res.data?.onboarding_video_url));
      })
      .catch(() => { /* mantém o vídeo padrão */ });
    return () => { active = false; };
  }, []);

  // Marca o onboarding como concluído (não bloqueia a navegação se falhar) e
  // leva direto ao dashboard.
  const goToPlatform = async () => {
    if (isLeaving) return;
    setIsLeaving(true);
    try {
      const res = await axios.post(
        `${API}/auth/onboarding`,
        { skipped: true },
        { withCredentials: true }
      );
      updateUser(res.data);
    } catch (err) {
      console.warn('Onboarding complete warning (non-blocking):', err?.message);
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-purple-800 to-purple-700 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-3xl">
        {/* Logo / Mascote */}
        <div className="text-center mb-6">
          <img
            src={GIRAFFE_MASCOT}
            alt="Simby"
            className="w-16 h-24 object-contain mx-auto mb-3"
          />
          <h1 className="text-3xl font-bold text-white">Bem-vinda à Simby</h1>
          <p className="text-purple-200 mt-1">
            Um minuto de boas-vindas e você já entra na plataforma
          </p>
        </div>

        {/* YouTube embed */}
        <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black mb-6">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
            title="Boas-vindas à Simby"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            className="bg-white text-purple-700 hover:bg-purple-50 text-lg px-10 py-6 font-semibold shadow-lg"
            onClick={goToPlatform}
            disabled={isLeaving}
          >
            {isLeaving ? 'Entrando…' : 'Ir para a plataforma'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <div className="mt-3">
            <button
              onClick={goToPlatform}
              disabled={isLeaving}
              className="text-purple-300 text-sm hover:text-white underline-offset-2 hover:underline"
            >
              Pular vídeo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
